import { firebase } from '../config/firebase.config';

export type SurveyQuestionType = 'single' | 'multiple' | 'text';
export type SurveyAnswer = string | string[];

export interface SessionSurveyQuestion {
  id: string;
  prompt: string;
  type: SurveyQuestionType;
  required: boolean;
  options: string[];
}

export interface SessionSurveyConfig {
  id: string;
  eventcode: string;
  sessionid: string;
  title: string;
  description: string;
  active: boolean;
  questions: SessionSurveyQuestion[];
  updatedAt?: any;
  updatedBy?: string;
}

export interface SessionSurveyResponse {
  userId: string;
  eventcode: string;
  sessionid: string;
  answers: Record<string, SurveyAnswer>;
  submittedAt?: any;
  updatedAt?: any;
}

export interface SurveyOptionResult {
  option: string;
  count: number;
  percentage: number;
}

export interface SurveyQuestionResult {
  questionId: string;
  prompt: string;
  type: SurveyQuestionType;
  answeredCount: number;
  options: SurveyOptionResult[];
  textAnswers: string[];
}

const normalizeEventCode = (eventCode: string): string =>
  eventCode.trim().toUpperCase().replace(/[^A-Z0-9]/g, '');

const normalizeSessionId = (sessionId: string | number): string => String(sessionId).trim();

export const getSessionSurveyDocumentId = (
  eventCode: string,
  sessionId: string | number
): string => `${encodeURIComponent(normalizeEventCode(eventCode))}__${encodeURIComponent(normalizeSessionId(sessionId))}`;

export const createSurveyQuestion = (type: SurveyQuestionType = 'single'): SessionSurveyQuestion => ({
  id: `question_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
  prompt: '',
  type,
  required: true,
  options: type === 'text' ? [] : ['', '']
});

export const createEmptySessionSurvey = (
  eventCode: string,
  sessionId: string | number
): SessionSurveyConfig => ({
  id: getSessionSurveyDocumentId(eventCode, sessionId),
  eventcode: normalizeEventCode(eventCode),
  sessionid: normalizeSessionId(sessionId),
  title: 'Encuesta de la sesión',
  description: '',
  active: false,
  questions: [createSurveyQuestion()]
});

const cleanQuestion = (question: SessionSurveyQuestion): SessionSurveyQuestion => ({
  id: question.id,
  prompt: question.prompt.trim(),
  type: question.type,
  required: Boolean(question.required),
  options: question.type === 'text'
    ? []
    : question.options.map(option => option.trim()).filter(Boolean)
});

export const validateSessionSurvey = (survey: SessionSurveyConfig): string[] => {
  const errors: string[] = [];
  if (!survey.title.trim()) errors.push('La encuesta debe tener un título.');
  if (survey.questions.length === 0) errors.push('Agregue al menos una pregunta.');
  if (survey.questions.length > 20) errors.push('La encuesta admite un máximo de 20 preguntas.');
  if (new Set(survey.questions.map(question => question.id)).size !== survey.questions.length) {
    errors.push('La encuesta contiene identificadores de pregunta repetidos.');
  }

  survey.questions.forEach((question, index) => {
    if (!question.prompt.trim()) errors.push(`La pregunta ${index + 1} no tiene texto.`);
    if (question.prompt.trim().length > 250) errors.push(`La pregunta ${index + 1} supera 250 caracteres.`);
    if (question.type !== 'text') {
      const options = question.options.map(option => option.trim()).filter(Boolean);
      if (options.length < 2) errors.push(`La pregunta ${index + 1} requiere al menos dos opciones.`);
      if (options.length > 10) errors.push(`La pregunta ${index + 1} admite un máximo de 10 opciones.`);
      if (new Set(options.map(option => option.toLowerCase())).size !== options.length) {
        errors.push(`La pregunta ${index + 1} contiene opciones repetidas.`);
      }
    }
  });
  return errors;
};

export const validateSurveyAnswers = (
  survey: SessionSurveyConfig,
  answers: Record<string, SurveyAnswer>
): string[] => {
  const errors: string[] = [];
  survey.questions.forEach((question, index) => {
    const answer = answers[question.id];
    const isEmpty = Array.isArray(answer) ? answer.length === 0 : !String(answer || '').trim();
    if (question.required && isEmpty) errors.push(`Responda la pregunta ${index + 1}.`);
    if (!isEmpty && question.type === 'single' &&
        (Array.isArray(answer) || !question.options.includes(String(answer)))) {
      errors.push(`La respuesta ${index + 1} no corresponde a una opción válida.`);
    }
    if (!isEmpty && question.type === 'multiple') {
      const selected = Array.isArray(answer) ? answer : [];
      if (!Array.isArray(answer) || selected.some(value => !question.options.includes(value)) ||
          new Set(selected).size !== selected.length) {
        errors.push(`La respuesta ${index + 1} contiene opciones inválidas.`);
      }
    }
    if (!isEmpty && question.type === 'text' && Array.isArray(answer)) {
      errors.push(`La respuesta ${index + 1} debe ser texto.`);
    }
    if (question.type === 'text' && String(answer || '').length > 2000) {
      errors.push(`La respuesta ${index + 1} supera 2000 caracteres.`);
    }
  });
  return errors;
};

export const aggregateSurveyResults = (
  survey: SessionSurveyConfig,
  responses: SessionSurveyResponse[]
): SurveyQuestionResult[] => survey.questions.map(question => {
  const values = responses
    .map(response => response.answers?.[question.id])
    .filter(value => Array.isArray(value) ? value.length > 0 : Boolean(String(value || '').trim()));
  const answeredCount = values.length;

  if (question.type === 'text') {
    return {
      questionId: question.id,
      prompt: question.prompt,
      type: question.type,
      answeredCount,
      options: [],
      textAnswers: values.map(value => String(value).trim()).filter(Boolean)
    };
  }

  const options = question.options.map(option => {
    const count = values.reduce((total, value) => {
      const selected = Array.isArray(value) ? value : [String(value)];
      return total + (selected.includes(option) ? 1 : 0);
    }, 0);
    return {
      option,
      count,
      percentage: answeredCount > 0 ? Math.round((count / answeredCount) * 100) : 0
    };
  });
  return {
    questionId: question.id,
    prompt: question.prompt,
    type: question.type,
    answeredCount,
    options,
    textAnswers: []
  };
});

class SessionSurveyService {
  private getSurveyRef(eventCode: string, sessionId: string | number) {
    return firebase.firestore()
      .collection('session_surveys')
      .doc(getSessionSurveyDocumentId(eventCode, sessionId));
  }

  subscribeSurvey(
    eventCode: string,
    sessionId: string | number,
    onValue: (survey: SessionSurveyConfig | null) => void,
    onError: (error: Error) => void
  ): () => void {
    return this.getSurveyRef(eventCode, sessionId).onSnapshot(snapshot => {
      if (!snapshot.exists) {
        onValue(null);
        return;
      }
      onValue({ id: snapshot.id, ...(snapshot.data() as Omit<SessionSurveyConfig, 'id'>) });
    }, onError);
  }

  async saveSurvey(survey: SessionSurveyConfig): Promise<void> {
    const currentUser = firebase.auth().currentUser;
    if (!currentUser) throw new Error('AUTHENTICATION_REQUIRED');
    const errors = validateSessionSurvey(survey);
    if (errors.length > 0) throw new Error(errors[0]);

    await this.getSurveyRef(survey.eventcode, survey.sessionid).set({
      eventcode: normalizeEventCode(survey.eventcode),
      sessionid: normalizeSessionId(survey.sessionid),
      title: survey.title.trim(),
      description: survey.description.trim(),
      active: Boolean(survey.active),
      questions: survey.questions.map(cleanQuestion),
      updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
      updatedBy: currentUser.uid
    }, { merge: true });
  }

  subscribeOwnResponse(
    eventCode: string,
    sessionId: string | number,
    onValue: (response: SessionSurveyResponse | null) => void,
    onError: (error: Error) => void
  ): () => void {
    const currentUser = firebase.auth().currentUser;
    if (!currentUser) {
      onValue(null);
      return () => undefined;
    }
    return this.getSurveyRef(eventCode, sessionId)
      .collection('responses')
      .doc(currentUser.uid)
      .onSnapshot(snapshot => {
        onValue(snapshot.exists ? snapshot.data() as SessionSurveyResponse : null);
      }, onError);
  }

  async submitResponse(
    survey: SessionSurveyConfig,
    answers: Record<string, SurveyAnswer>
  ): Promise<void> {
    const currentUser = firebase.auth().currentUser;
    if (!currentUser) throw new Error('AUTHENTICATION_REQUIRED');
    if (!survey.active) throw new Error('SURVEY_NOT_ACTIVE');
    const surveyRef = this.getSurveyRef(survey.eventcode, survey.sessionid);
    const responseRef = surveyRef.collection('responses').doc(currentUser.uid);

    // La transacción evita que dos envíos simultáneos puedan reemplazarse entre
    // sí. También vuelve a consultar la configuración publicada para impedir un
    // voto si el moderador acaba de desactivar o modificar la encuesta.
    await firebase.firestore().runTransaction(async transaction => {
      const currentSurveySnapshot = await transaction.get(surveyRef);
      if (!currentSurveySnapshot.exists) throw new Error('SURVEY_NOT_FOUND');

      const currentSurvey = {
        id: currentSurveySnapshot.id,
        ...(currentSurveySnapshot.data() as Omit<SessionSurveyConfig, 'id'>)
      };
      if (!currentSurvey.active) throw new Error('SURVEY_NOT_ACTIVE');

      const existingResponseSnapshot = await transaction.get(responseRef);
      if (existingResponseSnapshot.exists) throw new Error('SURVEY_ALREADY_SUBMITTED');

      const errors = validateSurveyAnswers(currentSurvey, answers);
      if (errors.length > 0) throw new Error(errors[0]);

      const cleanAnswers: Record<string, SurveyAnswer> = {};
      currentSurvey.questions.forEach(question => {
        const answer = answers[question.id];
        cleanAnswers[question.id] = Array.isArray(answer)
          ? answer.map(value => String(value).trim()).filter(Boolean)
          : String(answer || '').trim();
      });

      transaction.set(responseRef, {
        userId: currentUser.uid,
        eventcode: normalizeEventCode(currentSurvey.eventcode),
        sessionid: normalizeSessionId(currentSurvey.sessionid),
        answers: cleanAnswers,
        submittedAt: firebase.firestore.FieldValue.serverTimestamp(),
        updatedAt: firebase.firestore.FieldValue.serverTimestamp()
      });
    });
  }

  subscribeResponses(
    eventCode: string,
    sessionId: string | number,
    onValue: (responses: SessionSurveyResponse[]) => void,
    onError: (error: Error) => void
  ): () => void {
    return this.getSurveyRef(eventCode, sessionId)
      .collection('responses')
      .onSnapshot(snapshot => {
        onValue(snapshot.docs.map(doc => doc.data() as SessionSurveyResponse));
      }, onError);
  }
}

export const sessionSurveyService = new SessionSurveyService();
